import { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { DetailDrawer } from "@/components/common/DetailDrawer";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSpreadsheet, Download, Eye, Loader2, BarChart3, ShoppingBag,
  Calculator, ReceiptText, ShieldCheck, DatabaseBackup, Boxes,
} from "lucide-react";

const REPORTS = [
  { id: "sales", title: "Sales Report", desc: "Revenue & invoice summary by period.", icon: ShoppingBag, preview: "/sales-reports/summary", export: "/sales-reports/export" },
  { id: "costing", title: "Costing Report", desc: "Current vs decided cost, variance & margin.", icon: Calculator, preview: "/costing-reports/summary", export: "/costing-reports/export" },
  { id: "gst-purchase", title: "Purchase GST Report", desc: "Input GST from purchase bills.", icon: ReceiptText, preview: "/purchase-bills/gst-report", export: "/purchase-bills/gst-report/export" },
  { id: "po-gst", title: "PO GST Summary", desc: "GST breakdown across purchase orders.", icon: ReceiptText, preview: "/purchase-orders/gst-summary" },
  { id: "requirement", title: "Consolidated Requirement", desc: "Ingredient requirement from open orders.", icon: Boxes, preview: "/orders/requirement", export: "/orders/requirement/export" },
  { id: "integrity", title: "Data Integrity Report", desc: "Cross-module consistency checks.", icon: ShieldCheck, preview: "/integrity/report", export: "/integrity/export" },
  { id: "backup-excel", title: "Full Excel Backup", desc: "Download all master & transaction data.", icon: DatabaseBackup, export: "/export/excel" },
  { id: "backup-json", title: "JSON Backup", desc: "Machine-readable full data backup.", icon: DatabaseBackup, export: "/export/backup" },
];

export default function Reports() {
  const [busy, setBusy] = useState(null);
  const [drawer, setDrawer] = useState({ open: false, row: null, title: "" });

  const doExport = async (r) => {
    setBusy(`${r.id}-export`);
    try {
      const res = await api.get(r.export, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const ext = (res.headers["content-type"] || "").includes("json") ? "json" : "xlsx";
      const filename = m ? m[1] : `${r.id}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${r.title} downloaded`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Export failed"));
    } finally {
      setBusy(null);
    }
  };

  const doPreview = async (r) => {
    setBusy(`${r.id}-preview`);
    try {
      const res = await api.get(r.preview);
      const data = res.data;
      const row = Array.isArray(data) ? { records: data } : data;
      setDrawer({ open: true, row, title: r.title });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" subtitle="Preview or export ERP reports. Exports stream directly from the backend." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.id} data-testid={`report-card-${r.id}`} className="flex flex-col rounded-xl border border-stone-200 bg-white p-5 shadow-sm transition-all hover:border-amber-300">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700"><Icon className="h-5 w-5" /></div>
                <div className="font-display text-base font-semibold text-stone-900">{r.title}</div>
              </div>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{r.desc}</p>
              <div className="mt-4 flex gap-2">
                {r.preview && (
                  <Button data-testid={`btn-preview-${r.id}`} variant="outline" size="sm" className="gap-1.5" disabled={busy === `${r.id}-preview`} onClick={() => doPreview(r)}>
                    {busy === `${r.id}-preview` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
                  </Button>
                )}
                {r.export && (
                  <Button data-testid={`btn-export-${r.id}`} size="sm" className="gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={busy === `${r.id}-export`} onClick={() => doExport(r)}>
                    {busy === `${r.id}-export` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <DetailDrawer open={drawer.open} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} title={drawer.title} row={drawer.row} />
    </div>
  );
}
